import { nmrLipoProfileVisible } from './nmrLipoProfileVisibility';
import { vldlVisible } from './vldlVisibility';

/**
 * Whether a marker LabSmarts ships is actually drawn in THIS environment.
 *
 * A prebuilt catalog marker and a shipped field can name the same row. That is
 * not an accident: a panel is the live route to a marker until the shipped
 * field is finished, and two of them are mid-flight right now, visible on dev
 * and slava and held from prod (`nmrLipoProfileVisible`, `vldlVisible`). The
 * catalog note on NMR_LIPOPROFILE_PANEL explains why the panel stays rather
 * than being withdrawn ahead of its replacement.
 *
 * So "does LabSmarts chart this?" has no single answer, and anything deciding
 * what to do about a duplicate has to ask per environment:
 *
 *   - held (prod today): the catalog panel IS the route. Offer it.
 *   - released (dev, slava): the shipped field is the route, and adding the
 *     catalog marker would leave the practitioner holding two of the same
 *     marker on one test, free to disagree.
 *
 * An attribute with no entry here is a shipped marker with no flag on it,
 * which is charted everywhere. That is the right default: a later annotation
 * naming a field nobody gated should read as "LabSmarts draws this", not as
 * "hidden".
 *
 * Keyed on the `Bloodwork` attribute rather than the printed name, because the
 * printed names are the thing that varies (`HDL-P (Total)`, `VLDL Cholesterol
 * Cal`) and the attribute is the thing that does not.
 */
const GATES: { [attribute: string]: () => boolean } = {
  // The nine NMR LipoProfile rows. They ship and they hide together.
  ldlParticleNumber: nmrLipoProfileVisible,
  hdlParticleNumber: nmrLipoProfileVisible,
  smallLdlParticleNumber: nmrLipoProfileVisible,
  ldlSize: nmrLipoProfileVisible,
  largeVldlParticleNumber: nmrLipoProfileVisible,
  largeHdlParticleNumber: nmrLipoProfileVisible,
  vldlSize: nmrLipoProfileVisible,
  hdlSize: nmrLipoProfileVisible,
  lpIrScore: nmrLipoProfileVisible,
  // Labcorp's calculated VLDL. Released 2026-10-28, closed again 2026-10-30
  // while the ranges are finished, so it is held from prod today.
  vLDL: vldlVisible,
};

export function isShippedMarkerCharted(attribute?: string): boolean {
  if (!attribute) {
    return false;
  }
  const gate = GATES[attribute];
  return gate ? gate() : true;
}

/** The gated attributes, for the tests that keep this in step with the catalog. */
export const GATED_SHIPPED_ATTRIBUTES = Object.keys(GATES);
